import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from './AuthGate';
import AdminPanel from './AdminPanel';
import Icon from './Icon';
import { managerInitials } from './RelationshipManagersField';
import './RelationshipManagers.css';
import './AdminPanel.css';

const ROLE_NAMES = { admin: 'Admin', relationship_manager: 'Relationship manager' };

const CLEARED_KEYS = ['preferredZoomLevel', 'userPreferences', 'filterSettings', 'viewPreferences'];

const clearLocalSettings = () => {
  if (!window.confirm('Clear saved zoom preferences and other local settings?')) return;
  CLEARED_KEYS.forEach(key => localStorage.removeItem(key));
  window.location.reload();
};

const Avatar = ({ user }) => (user.picture_url ? (
  <img className="rm-avatar" src={user.picture_url} alt="" referrerPolicy="no-referrer" />
) : (
  <span className="rm-avatar" aria-hidden="true">{managerInitials(user.name || user.email)}</span>
));

/** The signed-in person's avatar in the top bar: who they are, admin tools, and signing out. */
const AccountMenu = () => {
  const { user, isAdmin, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [open]);

  if (!user) return null;
  const label = user.name || user.email;

  return (
    <>
      <button
        type="button"
        className="account-button"
        onClick={() => setOpen(current => !current)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${label}`}
        title={label}
      >
        <Avatar user={user} />
      </button>
      {open && createPortal(
        <>
          <div className="account-menu-overlay" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="account-menu" role="menu" aria-label="Account">
            <div className="account-menu__name">{label}</div>
            {user.name && <div className="account-menu__email">{user.email}</div>}
            <div className="account-menu__roles">{user.roles.map(role => ROLE_NAMES[role] || role).join(' · ')}</div>
            {isAdmin && (
              <button
                type="button"
                role="menuitem"
                className="rm-button rm-button--quiet"
                onClick={() => {
                  setOpen(false);
                  setAdminOpen(true);
                }}
              >
                <Icon name="shield" /> Users and activity
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              className="rm-button rm-button--quiet"
              onClick={() => {
                setOpen(false);
                clearLocalSettings();
              }}
            >
              <Icon name="settings" /> Clear local settings
            </button>
            <button
              type="button"
              role="menuitem"
              className="rm-button rm-button--quiet"
              onClick={() => {
                setOpen(false);
                signOut();
              }}
            >
              <Icon name="log-out" /> Sign out
            </button>
          </div>
        </>,
        document.body
      )}
      <AdminPanel isOpen={adminOpen} onClose={() => setAdminOpen(false)} />
    </>
  );
};

export default AccountMenu;
